import React, { useState, useEffect } from 'react';
import { getOCELMetadata, apiClient } from '../../../../services/apiClient';

const EMPTY_FILTER = { enabled: false, mode: 'include', matchType: 'any', conditions: [] };

const TileEventAttributeFilter = ({
  value = EMPTY_FILTER,
  onChange,
  activeFile,
  disabled = false,
}) => {
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});
  const [loadingAttrs, setLoadingAttrs] = useState(false);
  const [loadingValues, setLoadingValues] = useState({});

  useEffect(() => {
    if (!activeFile) return;
    let cancelled = false;
    setLoadingAttrs(true);
    getOCELMetadata(activeFile)
      .then((metadata) => {
        if (cancelled) return;
        const attrs = Array.isArray(metadata?.event_attribute_names)
          ? metadata.event_attribute_names
          : Object.keys(metadata?.event_attribute_names || {});
        setAvailableAttributes(attrs);
      })
      .catch(() => { if (!cancelled) setAvailableAttributes([]); })
      .finally(() => { if (!cancelled) setLoadingAttrs(false); });
    return () => { cancelled = true; };
  }, [activeFile]);

  const fetchValues = (attribute) => {
    if (!activeFile || !attribute || attributeValues[attribute]) return;
    setLoadingValues((prev) => ({ ...prev, [attribute]: true }));
    apiClient
      .get(`/ocel/event-attributes/${encodeURIComponent(attribute)}/values`, {
        params: { file_type: activeFile },
      })
      .then((res) => {
        setAttributeValues((prev) => ({ ...prev, [attribute]: res.data || [] }));
      })
      .catch(() => {
        setAttributeValues((prev) => ({ ...prev, [attribute]: [] }));
      })
      .finally(() => {
        setLoadingValues((prev) => ({ ...prev, [attribute]: false }));
      });
  };

  const update = (patch) => onChange({ ...value, ...patch });

  const handleConditionChange = (index, field, newVal) => {
    const next = [...value.conditions];
    next[index] = { ...next[index], [field]: newVal };
    if (field === 'attribute') {
      next[index].value = '';
      if (newVal) fetchValues(newVal);
    }
    update({ conditions: next });
  };

  const handleAddCondition = () => {
    update({ conditions: [...value.conditions, { attribute: '', value: '' }] });
  };

  const handleRemoveCondition = (index) => {
    update({ conditions: value.conditions.filter((_, i) => i !== index) });
  };

  if (loadingAttrs) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Event Attribute Filter</label>
        <div className="text-sm text-gray-500 italic">Loading attributes…</div>
      </div>
    );
  }

  const isControlDisabled = disabled || !value.enabled;

  return (
    <div className="space-y-3">
      {/* Header + Enabled toggle */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Event Attribute Filter
          {value.enabled && value.conditions.filter((c) => c.attribute && c.value).length > 0 && (
            <span className="ml-2 text-xs text-gray-500">
              ({value.conditions.filter((c) => c.attribute && c.value).length} condition(s))
            </span>
          )}
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
            disabled={disabled}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Enabled</span>
        </label>
      </div>

      {/* Mode */}
      <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
        <span className="text-xs font-medium text-gray-700">Mode:</span>
        {['include', 'exclude'].map((m) => (
          <label key={m} className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="tile-eaf-mode"
              value={m}
              checked={value.mode === m}
              onChange={() => update({ mode: m })}
              disabled={isControlDisabled}
              className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{m === 'include' ? 'Include' : 'Exclude'}</span>
          </label>
        ))}
      </div>

      {/* Match */}
      <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
        <span className="text-xs font-medium text-gray-700">Match:</span>
        {['any', 'all'].map((m) => (
          <label key={m} className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="tile-eaf-match"
              value={m}
              checked={value.matchType === m}
              onChange={() => update({ matchType: m })}
              disabled={isControlDisabled}
              className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{m === 'any' ? 'Any' : 'All'}</span>
          </label>
        ))}
      </div>

      {/* Conditions */}
      <div className="space-y-2">
        {value.conditions.map((cond, idx) => {
          const vals = attributeValues[cond.attribute] || [];
          const isLoadingVals = loadingValues[cond.attribute];
          return (
            <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
              <div className="flex-1 space-y-2">
                <select
                  value={cond.attribute}
                  onChange={(e) => handleConditionChange(idx, 'attribute', e.target.value)}
                  disabled={isControlDisabled || availableAttributes.length === 0}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select attribute…</option>
                  {availableAttributes.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>

                {isLoadingVals ? (
                  <div className="text-xs text-gray-500 italic py-1">Loading values…</div>
                ) : vals.length > 0 ? (
                  <select
                    value={cond.value}
                    onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                    disabled={isControlDisabled || !cond.attribute}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select value…</option>
                    {vals.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={cond.value}
                    onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                    disabled={isControlDisabled || !cond.attribute}
                    placeholder="Enter value…"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveCondition(idx)}
                disabled={isControlDisabled}
                className="mt-1 text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                title="Remove condition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleAddCondition}
        disabled={isControlDisabled}
        className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        + Add condition
      </button>

      {!value.enabled && (
        <div className="text-xs text-gray-500 italic">Enable filter to add conditions</div>
      )}
    </div>
  );
};

export default TileEventAttributeFilter;
